/**
 * Páginas que caem nesse código.
 * /poder/(apostas-da-semana|impacto-nas-instituicoes|risco-politico|alertas)
 * /tributos/(apostas-da-semana|direto-do-carf|direto-da-corte|direto-do-legislativo|matinal|relatorio-especial)
 * /saude/(apostas-da-semana|bastidores-da-saude|direto-da-anvisa-ans|direto-da-corte|direto-do-legislativo|matinal|relatorio-especial|alertas)
 * /trabalhista/(apostas-da-semana|direto-da-corte|direto-da-fonte|giro-nos-trts|relatorio-especial)
 * /tecnologia/(apostas-da-semana|matinal|resumo-da-semana|relatorios-especiais|panorama-regulatorio|relatorio-especial|alertas)
 */
import { API_HEADER_FOOTER, HOURS24 } from '@App/api/api'
import BreadcrumbComponentJU, {
    makeBreadItemsFromOS
} from '@App/libs/components/ds/Breadcrumb'
import ListContentFromAlertas from '@App/libs/components/editoria/ListContentFromAlertas'
import ListComponent from '@App/libs/components/list/ListComponent'
import { LoadingSearchContent } from '@App/libs/components/loading/Loading.component'
import Metadata from '@App/libs/components/Metadata.component'
import { PageTypeEnum } from '@App/libs/interfaces'
import { fetchEditorialContent } from '@App/libs/services/editoria.service'
import { getDescriptionFromTagOrCategoryName } from '@App/libs/utils/constants/global'
import { JOTAMetadata } from '@App/libs/utils/constants/metadata'
import { AlertaListItem } from '@App/libs/utils/geral'
import { colors, fonts, MD_PX, SM_PX, spaces } from '@App/static/styles/global'
import { useRouter } from 'next/router'
import {
    GetStaticPaths,
    GetStaticPropsContext,
    GetStaticPropsResult,
    NextPage
} from 'next/types'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { FooterComponent, PaginationDS } from '../../../../../libs/components'
import HeaderComponent from '../../../../../libs/components/header'
import { Body } from '../../../../../libs/components/header/Header.styled'
import { FooterIH, HeaderIH, SearchIH } from '../../../../../libs/interfaces'
import { PaginationContainer } from '../../../../../libs/style/search.style'
import { getTitleByParams } from '../../../../../libs/utils/editoriaUtils'

const SearchPage: NextPage<{
    header: HeaderIH
    footer: FooterIH
    vertical: string
    category: string
}> = ({ header, footer, vertical, category }) => {
    const router = useRouter()
    const pageTitle = getTitleByParams(category)
    const [content, setContent] = useState<SearchIH[]>([])
    const [totalValue, setTotalValue] = useState<number>(0)
    const [page, setPage] = useState<number>(1)
    const [postsAlertas, setPostsAlertas] = useState<AlertaListItem[]>([])
    const [finishGetContent, setFinishGetContent] = useState<boolean>(false)
    const pageFromParams =
        parseInt(router?.query?.page as string) ||
        parseInt(router.asPath.split('=')[1] || '1')
    const [show, setShow] = useState<boolean>(false)

    useEffect(() => {
        if (!router.isReady) return

        const currentPage = Number.parseInt(router.query.page as string) || 1

        setPage(currentPage)

        let didCancel = false

        fetchEditorialContent(vertical, category, currentPage).then(
            (result) => {
                if (didCancel) return
                if (result.isAlertList)
                    setPostsAlertas(result.items as AlertaListItem[])
                else setContent(result.items as SearchIH[])
                setFinishGetContent(true)
                setTotalValue(result.totalPages)
            }
        )

        setTimeout(() => {
            setShow(true)
        }, 500)

        return () => {
            didCancel = true
        }
    }, [router.isReady, router.query.page, vertical, category])

    const displayContent = (
        postsAlertas?: AlertaListItem[],
        content?: SearchIH[]
    ) => {
        if (postsAlertas && postsAlertas.length > 0) {
            return (
                <ListContentFromAlertas
                    items={postsAlertas as AlertaListItem[]}
                    alertType={vertical}
                />
            )
        }
        return <ListComponent content={content ?? []} type='os' />
    }

    if (!finishGetContent && !show && !router.isReady) {
        return (
            <LoadingComponent>
                {/* eslint-disable-next-line new-cap */}
                {LoadingSearchContent()}
            </LoadingComponent>
        )
    }

    return (
        <div className='page' data-page-type='pro-categories'>
            <Metadata
                title={`JOTA PRO - ${vertical.toUpperCase()} - ${category.replaceAll('-', ' ').toUpperCase()}`}
                description={getDescriptionFromTagOrCategoryName(category)}
                breadcrumb={makeBreadItemsFromOS(router.asPath)}
                pageType={PageTypeEnum.CATEGORY}
                image={JOTAMetadata.urlLogoPro}
                pagination={{
                    page: pageFromParams,
                    totalPages: totalValue
                }}
            />
            <Body>
                {header && show && (
                    <HeaderComponent
                        wpMenu={header.menu}
                        ctaPRO={header.pro}
                        showAcessibility={header.enableAccessibility}
                        signupLink={header.assinePage}
                    />
                )}
                {postsAlertas.length == 0 && (
                    <EditoriaHeader>
                        {category && pageTitle && show && (
                            <BreadcrumbComponentJU
                                items={makeBreadItemsFromOS(router.asPath)}
                            />
                        )}
                        <EditoriaTitle>{pageTitle}</EditoriaTitle>
                    </EditoriaHeader>
                )}

                {displayContent(postsAlertas, content)}

                <PaginationContainer>
                    <PaginationDS
                        currentPage={page}
                        totalPages={totalValue}
                        handleChange={(e: any, currentPage: number) => {
                            window.open(
                                `/${vertical}/${category}?page=${currentPage}`,
                                '_self'
                            )
                        }}
                        color='jotaOrange'
                    />
                </PaginationContainer>

                {footer && <FooterComponent footer={footer} />}
            </Body>
        </div>
    )
}

export default SearchPage

export const getStaticPaths: GetStaticPaths<{
    editoria: string
}> = async () => {
    return {
        paths: [],
        fallback: 'blocking'
    }
}

/**
 * getStaticProps
 * @param {unknow} context
 * @return {Promise<GetStaticPropsResult<HomePagePropsInterface>>}
 */
export async function getStaticProps(
    context: GetStaticPropsContext<{ vertical: string; category: string }>
): Promise<GetStaticPropsResult<unknown>> {
    if (!API_HEADER_FOOTER) {
        throw new Error('API_HEADER_FOOTER is not defined')
    }

    const resHeaderFooter = await fetch(API_HEADER_FOOTER, {
        cache: 'no-cache'
    })
    const { header, footer } = await resHeaderFooter.json()

    return {
        props: {
            header,
            footer,
            vertical: context.params?.vertical ?? null,
            category: context.params?.category ?? null
        },
        revalidate: HOURS24
    }
}

export const EditoriaTitle = styled.h1<{ isEmpty?: boolean }>`
    ${fonts.jotaHeading04};
    color: ${colors.black.jotaBlack};
    margin: 0 0 0 0;
    padding-bottom: ${(props) => props.isEmpty && spaces.jotaSpacing8};
    padding-top: ${spaces.jotaSpacing12};

    border-bottom: ${(props) =>
        props.isEmpty && `1px solid ${colors.transparent.jotaTGrayLight40}`};

    @media only screen and (max-width: ${MD_PX}) {
        margin-top: 0;
    }
`
export const EditoriaHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-inline: 44px;
    padding-top: 0px;
    box-sizing: border-box;
    border-bottom: 1px solid ${colors.transparent.jotaTGrayLight40};
    z-index: -2;
    margin-top: ${spaces.jotaSpacing44};

    @media only screen and (max-width: ${MD_PX}) {
        flex-direction: column;
        align-items: flex-start;
        margin-inline: 36px;
    }

    @media only screen and (max-width: ${SM_PX}) {
        margin-inline: 16px;
        margin-top: ${spaces.jotaSpacing24};
    }
`
export const LoadingComponent = styled.div`
    margin-inline: 44px;
`
